import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Hammer, Package, Search } from "lucide-react";
import Reveal from "@/components/Reveal";
import { EMBLEM_URL } from "@/data/content";
import { CASE_STUDIES } from "@/data/caseStudies";
import { RELEASES } from "@/data/releases";

const Chapter = ({ code, icon: Icon, chapter, index }) => (
  <section data-testid={`chapter-${code}`} className={index > 0 ? "border-t border-[#282D37]/70 bg-[#0D0F12]" : ""}>
    <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-24">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.8fr] lg:gap-20">
        <Reveal>
          <div className="flex items-center gap-3">
            <Icon size={18} className="text-[#E52E2E]" />
            <span className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">{code}</span>
          </div>
          <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
            {chapter.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#C6CAD2] sm:text-lg">{chapter.intro}</p>
        </Reveal>
        <div className="space-y-px">
          {chapter.points.map((point, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <div className="flex gap-5 border border-[#282D37] bg-[#121418] p-6">
                <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#E52E2E]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-sm leading-relaxed text-[#9EA4B0] sm:text-base">{point}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const CaseStudy = () => {
  const { slug } = useParams();
  const study = CASE_STUDIES.find((c) => c.slug === slug);
  const release = RELEASES.find((r) => r.slug === slug);

  if (!study) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-32 text-center">
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">SIGNAL LOST</p>
        <h1 className="mt-4 font-display text-4xl font-black uppercase text-[#F4F5F7]">Case study not found</h1>
        <Link
          to="/services"
          data-testid="study-notfound-back"
          className="mt-8 border border-[#3A404E] px-8 py-4 font-mono text-xs font-bold tracking-[0.25em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
        >
          BACK TO SERVICES
        </Link>
      </div>
    );
  }

  return (
    <div className="relative pt-32">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        style={{
          background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
        }}
      />
      <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <Link
            to="/services"
            data-testid="study-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
          >
            <ArrowLeft size={14} /> SERVICES
          </Link>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <img src={EMBLEM_URL} alt="Arsenal Forge emblem" className="emblem-glow w-16 sm:w-20" />
            <div>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">{study.label}</p>
              <h1 className="mt-2 font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F4F5F7] sm:text-7xl">
                {study.displayName}
              </h1>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2 font-mono text-[10px] font-bold tracking-[0.25em]">
            {[study.game.toUpperCase(), study.client.toUpperCase(), study.category.toUpperCase(), `v${study.version}`, study.status].map(
              (b) => (
                <span key={b} className="border border-[#282D37] px-3 py-1.5 text-[#9EA4B0]">
                  {b}
                </span>
              )
            )}
          </div>
          <p className="mt-8 max-w-3xl font-display text-2xl font-semibold uppercase leading-snug tracking-wide text-[#C6CAD2] sm:text-3xl">
            {study.hero}
          </p>
        </Reveal>
      </header>

      <div className="mt-10">
        <Chapter code="01 // THE PROBLEM" icon={Search} chapter={study.problem} index={0} />
        <Chapter code="02 // THE BUILD" icon={Hammer} chapter={study.build} index={1} />
        <Chapter code="03 // THE RESULT" icon={Package} chapter={study.results} index={2} />
      </div>

      {release?.video && (
        <section data-testid="study-evidence" className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">EVIDENCE // REAL BUILD FOOTAGE</p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
              Don't take our word for it
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 border border-[#282D37] bg-[#0A0B0D]">
              <div className="flex items-center gap-2.5 border-b border-[#282D37] px-4 py-2.5">
                <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-[#9EA4B0]">
                  REC · {study.name.toUpperCase()}_V{release.version} · IN-GAME CAPTURE
                </span>
              </div>
              <video
                data-testid="study-video"
                src={release.video}
                className="aspect-video w-full object-cover"
                muted
                loop
                playsInline
                controls
                preload="metadata"
              />
            </div>
          </Reveal>
          {release.screenshots.length > 0 && (
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {release.screenshots.map((s, i) => (
                <Reveal key={s.src} delay={0.06 * i}>
                  <img
                    src={s.src}
                    alt={s.caption}
                    data-testid={`study-shot-${i}`}
                    className="aspect-video w-full border border-[#282D37] object-cover object-top opacity-90 transition-opacity duration-500 hover:opacity-100"
                  />
                </Reveal>
              ))}
            </div>
          )}
        </section>
      )}

      <section data-testid="study-next" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-20">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">WHAT'S NEXT</p>
              <p className="mt-5 text-base leading-relaxed text-[#9EA4B0] sm:text-lg">{study.nextSteps}</p>
              <Link
                to={study.projectUrl}
                data-testid="study-view-project"
                className="group mt-8 inline-flex items-center gap-3 border border-[#3A404E] px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
              >
                {study.ctaLabel || "View the Project"}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="border border-[#E52E2E]/40 bg-[#0A0B0D] p-8 sm:p-10">
                <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">COMMISSION WORK LIKE THIS</p>
                <p className="mt-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-3xl">
                  Need a system built to this standard?
                </p>
                <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">
                  This is what a fixed-scope build looks like when it leaves the forge: scoped,
                  hardened in production, documented and shipped.
                </p>
                <Link
                  to="/contact?type=plugin"
                  data-testid="study-cta-contact"
                  className="mt-7 inline-block bg-[#E52E2E] px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#FF3B3B]"
                >
                  Start a Project
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudy;
